import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import EmailTemplateModal from './EmailTemplateModal';
import TestEmailModal from './TestEmailModal';

/**
 * Notification Settings Component
 * 
 * Manages email configurations and notification settings for admin users
 * 
 * @author SureCost Development Team
 * @version 1.0.0
 */
const NotificationSettings = () => {
    const [emailConfig, setEmailConfig] = useState({
        provider: 'brevo',
        apiKey: '',
        fromEmail: 'pedro@acqualytics.com',
        fromName: 'SureCost',
        replyTo: 'pedro@acqualytics.com',
        isConfigured: false
    });
    
    const [notificationSettings, setNotificationSettings] = useState({
        magicLinkEnabled: true, // Always enabled - required for authentication
        welcomeEmailEnabled: true
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentNotificationType, setCurrentNotificationType] = useState(null);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [testNotificationType, setTestNotificationType] = useState(null);
    const [showProviderWarning, setShowProviderWarning] = useState(false);

    useEffect(() => {
        loadNotificationConfig();
    }, []);

    const loadNotificationConfig = async () => {
        try {
            const response = await fetch('/api/admin/notifications');
            if (response.ok) {
                const data = await response.json();
                console.log('Loaded notification config:', data); // Debug log
                setEmailConfig(data.emailConfig || emailConfig);
                setNotificationSettings(data.notificationSettings || notificationSettings);
                setLastUpdated(data.lastUpdated ? new Date(data.lastUpdated) : null);
            }
        } catch (error) {
            console.error('Failed to load notification config:', error);
        }
    };

    const handleEmailConfigChange = (field, value) => {
        console.log('🔧 Email config change:', field, value);
        
        // Show warning and clear other fields when provider changes
        if (field === 'provider' && value !== emailConfig.provider) {
            setShowProviderWarning(true);
            // Auto-hide after 8 seconds
            setTimeout(() => {
                setShowProviderWarning(false);
            }, 8000);
            
            // Clear other fields when provider changes
            setEmailConfig(prev => ({
                ...prev,
                provider: value,
                apiKey: '',
                fromEmail: '',
                fromName: '',
                replyTo: '',
                isConfigured: false
            }));
            return;
        }
        
        setEmailConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNotificationToggle = (setting) => {
        setNotificationSettings(prev => ({
            ...prev,
            [setting]: !prev[setting]
        }));
    };

    const testEmailConfiguration = async () => {
        setIsLoading(true);
        try {
            // Use existing configuration if API key is already configured
            const testConfig = {
                ...emailConfig,
                apiKey: emailConfig.isConfigured ? 'configured' : emailConfig.apiKey
            };

            const response = await fetch('/api/admin/notifications/test', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(testConfig)
            });

            const result = await response.json();
            
            if (result.success) {
                alert('Test email sent successfully! Check your inbox.');
            } else {
                alert(`Test failed: ${result.message}`);
            }
        } catch (error) {
            console.error('Email test failed:', error);
            alert('Email test failed. Please check your configuration.');
        } finally {
            setIsLoading(false);
        }
    };

    const testNotification = async (type) => {
        setIsLoading(true);
        try {
            const testConfig = {
                ...emailConfig,
                apiKey: emailConfig.isConfigured ? 'configured' : emailConfig.apiKey,
                notificationType: type
            };

            const response = await fetch('/api/admin/notifications/test-type', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(testConfig)
            });

            const result = await response.json();
            
            if (result.success) {
                alert(`Test ${type === 'magicLink' ? 'Magic Link' : 'Welcome Email'} sent successfully! Check your inbox.`);
            } else {
                alert(`Test failed: ${result.message}`);
            }
        } catch (error) {
            console.error('Notification test failed:', error);
            alert('Notification test failed. Please check your configuration.');
        } finally {
            setIsLoading(false);
        }
    };

    const openCustomizeModal = (notificationType) => {
        setCurrentNotificationType(notificationType);
        setModalOpen(true);
    };

    const closeCustomizeModal = () => {
        setModalOpen(false);
        setCurrentNotificationType(null);
    };

    const handleTemplateSaved = () => {
        // Optionally refresh notification config or show success message
        console.log('Email template saved successfully');
    };

    const openTestModal = (notificationType) => {
        setTestNotificationType(notificationType);
        setTestModalOpen(true);
    };

    const closeTestModal = () => {
        setTestModalOpen(false);
        setTestNotificationType(null);
    };

    const sendTestEmail = async (email, notificationType) => {
        try {
            const requestBody = {
                provider: emailConfig.provider,
                apiKey: emailConfig.isConfigured ? 'configured' : emailConfig.apiKey,
                fromEmail: emailConfig.fromEmail,
                fromName: emailConfig.fromName,
                notificationType: notificationType,
                testEmail: email
            };
            
            console.log('🧪 Sending test email request:', requestBody);
            
            const response = await fetch('/api/admin/notifications/test-type', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();
            
            if (!response.ok) {
                throw new Error(result.message || 'Failed to send test email');
            }

            alert(`Test ${notificationType} email sent successfully to ${email}!`);
            return result;
        } catch (error) {
            console.error('Test email error:', error);
            throw error;
        }
    };

    const saveConfiguration = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/admin/notifications', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    emailConfig,
                    notificationSettings
                })
            });

            const result = await response.json();
            
            if (result.success) {
                setLastUpdated(new Date());
                alert('Notification settings saved successfully!');
            } else {
                alert(`Failed to save settings: ${result.message}`);
            }
        } catch (error) {
            console.error('Failed to save notification settings:', error);
            alert('Failed to save notification settings.');
        } finally {
            setIsLoading(false);
        }
    };

    const getProviderName = (provider) => {
        const providers = {
            'brevo': 'Brevo (Sendinblue)',
            'postmark': 'Postmark',
            'mailgun': 'Mailgun',
            'sendgrid': 'SendGrid'
        };
        return providers[provider] || provider;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
                    <p className="text-xs text-gray-500">Configure email providers and notification preferences</p>
                </div>
                
                {lastUpdated && (
                    <div className="text-xs text-gray-500">
                        Last updated: {lastUpdated.toLocaleString()}
                    </div>
                )}
            </div>

            {/* Email Configuration */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-sm font-semibold text-gray-900">Email Provider Configuration</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        {/* Email Provider */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                                Email Provider *
                            </label>
                            <div className="relative">
                                <select
                                    value={emailConfig.provider}
                                    onChange={(e) => handleEmailConfigChange('provider', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 transition-colors h-10 appearance-none"
                                    style={{ 
                                        fontSize: '14px',
                                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                                        backgroundPosition: 'right 0.5rem center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: '2.5rem'
                                    }}
                                >
                                    <option value="brevo">Brevo (Sendinblue)</option>
                                    <option value="postmark">Postmark</option>
                                    <option value="mailgun">Mailgun</option>
                                    <option value="sendgrid">SendGrid</option>
                                </select>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Current: {getProviderName(emailConfig.provider)}</p>
                        </div>

                        {/* API Key */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-medium text-gray-700">
                                    API Key *
                                </label>
                                {emailConfig.isConfigured && (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Configured
                                    </span>
                                )}
                            </div>
                            <input
                                type={emailConfig.isConfigured ? "text" : "password"}
                                value={emailConfig.apiKey || ''}
                                onChange={(e) => handleEmailConfigChange('apiKey', e.target.value)}
                                placeholder={emailConfig.isConfigured ? "API key is configured" : "Enter your API key"}
                                readOnly={emailConfig.isConfigured}
                                className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 transition-colors h-10 ${
                                    emailConfig.isConfigured ? 'bg-gray-50 text-gray-700' : ''
                                }`}
                            />
                            {/* Add spacing to match the provider field */}
                            <div className="h-5 mt-1"></div>
                        </div>

                        {/* From Email */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                                From Email *
                            </label>
                            <input
                                type="email"
                                value={emailConfig.fromEmail}
                                onChange={(e) => handleEmailConfigChange('fromEmail', e.target.value)}
                                placeholder="noreply@yourdomain.com"
                                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 transition-colors h-10"
                                style={{ fontSize: '14px' }}
                            />
                        </div>

                        {/* From Name */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                                From Name
                            </label>
                            <input
                                type="text"
                                value={emailConfig.fromName}
                                onChange={(e) => handleEmailConfigChange('fromName', e.target.value)}
                                placeholder="SureCost"
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 transition-colors h-10"
                            />
                        </div>

                        {/* Reply-To Email (spans both columns) */}
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                                Reply-To Email
                            </label>
                            <input
                                type="email"
                                value={emailConfig.replyTo}
                                onChange={(e) => handleEmailConfigChange('replyTo', e.target.value)}
                                placeholder="support@yourdomain.com"
                                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 transition-colors h-10"
                                style={{ fontSize: '14px' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Email Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                    <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                            * Required fields
                        </div>
                        
                    </div>
                </div>
            </div>

            {/* Notification Types */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-sm font-semibold text-gray-900">Notification Types</h3>
                    </div>
                    
                    <div className="space-y-4">
                        {/* Authentication Notifications */}
                        <div>
                            <h4 className="text-xs font-medium text-gray-700 mb-3">Authentication</h4>
                            <div className="space-y-4">
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 pr-4">
                                            <div className="text-sm font-medium text-gray-900">Magic Link Authentication</div>
                                            <div className="text-xs text-gray-500">Send secure sign-in links to users</div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200 rounded">
                                                Required
                                            </span>
                                            <button
                                                onClick={() => openCustomizeModal('magicLink')}
                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors border-0 outline-none focus:outline-none focus:ring-0"
                                                style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                Customize
                                            </button>
                                            <button
                                                onClick={() => openTestModal('magicLink')}
                                                disabled={isLoading}
                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors border-0 outline-none focus:outline-none focus:ring-0"
                                                style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                </svg>
                                                Test
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 pr-4">
                                            <div className="text-sm font-medium text-gray-900">Welcome Emails</div>
                                            <div className="text-xs text-gray-500">Send welcome messages to new users</div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                onClick={() => handleNotificationToggle('welcomeEmailEnabled')}
                                                className={`px-3 py-1 text-xs font-medium rounded border transition-colors ${
                                                    notificationSettings.welcomeEmailEnabled 
                                                        ? 'bg-blue-600 text-white border-blue-600' 
                                                        : 'bg-gray-200 text-gray-700 border-gray-300'
                                                }`}
                                            >
                                                {notificationSettings.welcomeEmailEnabled ? 'ON' : 'OFF'}
                                            </button>
                                            <button
                                                onClick={() => openCustomizeModal('welcome')}
                                                disabled={!notificationSettings.welcomeEmailEnabled}
                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors border-0 outline-none focus:outline-none focus:ring-0"
                                                style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                Customize
                                            </button>
                                            <button
                                                onClick={() => openTestModal('welcome')}
                                                disabled={!notificationSettings.welcomeEmailEnabled || isLoading}
                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors border-0 outline-none focus:outline-none focus:ring-0"
                                                style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                </svg>
                                                Test
                                            </button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>

                {/* Save Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                    <div className="flex items-center justify-end">
                        <button
                            onClick={saveConfiguration}
                            disabled={isLoading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors border-0 outline-none focus:outline-none focus:ring-0"
                            style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Save All Settings
                        </button>
                    </div>
                </div>
            </div>

            {/* Info Panel */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <h4 className="text-sm font-medium text-amber-900 mb-1">Email Configuration Notes</h4>
                        <ul className="text-xs text-amber-800 space-y-1">
                            <li>• Make sure your email provider API key has the necessary permissions</li>
                            <li>• The "From Email" address should be verified with your email provider</li>
                            <li>• Test your configuration before enabling notifications</li>
                            <li>• Changes take effect immediately after saving</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Email Template Customization Modal */}
            <EmailTemplateModal
                isOpen={modalOpen}
                onClose={closeCustomizeModal}
                notificationType={currentNotificationType}
                onSave={handleTemplateSaved}
            />

            {/* Test Email Modal */}
            <TestEmailModal
                isOpen={testModalOpen}
                onClose={closeTestModal}
                notificationType={testNotificationType}
                onSend={sendTestEmail}
            />

            {/* Provider Change Warning - Rendered via Portal */}
            {showProviderWarning && ReactDOM.createPortal(
                <div 
                    style={{
                        position: 'fixed',
                        top: '1rem',
                        right: '1rem',
                        maxWidth: '28rem',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        padding: '1rem',
                        zIndex: 9999
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ flexShrink: 0 }}>
                            <svg style={{ width: '1.25rem', height: '1.25rem', color: '#f59e0b' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div style={{ marginLeft: '0.75rem', flex: 1 }}>
                            <h3 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#92400e', margin: 0 }}>
                                Email Provider Changed
                            </h3>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#b45309' }}>
                                <p style={{ margin: 0 }}>
                                    Please ensure you configure the new email provider correctly. 
                                    <strong style={{ fontWeight: '600' }}> Magic link authentication is required to access the dashboard!</strong>
                                </p>
                            </div>
                        </div>
                        <div style={{ marginLeft: '1rem', flexShrink: 0 }}>
                            <button
                                onClick={() => setShowProviderWarning(false)}
                                style={{
                                    display: 'inline-flex',
                                    color: '#f59e0b',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#d97706'}
                                onMouseLeave={(e) => e.target.style.color = '#f59e0b'}
                            >
                                <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default NotificationSettings;
